import { observer } from "mobx-react-lite";
import useBreakpoint from "use-breakpoint";
import {
  Space,
  Table,
  Button,
  Input,
  Select,
  Flex,
  Dropdown,
  Checkbox,
  message,
  type MenuProps,
} from "antd";
import { SearchOutlined, EyeOutlined, HolderOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import {
  LayoutColumnSettingsRequest,
  LocalTask,
  TasksListBlock,
  UserColumn,
} from "../../../api/BoardVue";
import { useStore } from "../../../root-store-context";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Constants from "../../../helpers/constants";
dayjs.extend(utc);

type ColumnKey = string;

const ALLOWED_COLUMNS: ColumnKey[] = [
  "id",
  "title",
  "internalTaskStatus",
  "dueDate",
  "modified",
  "priority",
];

const formatDateCell = (value?: string | Date | null) =>
  value ? dayjs.utc(value).format("MM/DD/YYYY") : "";
const COLUMN_TITLES: Record<ColumnKey, string> = {
  id: "ID",
  title: "Title",
  startDate: "Start Date",
  dueDate: "Due Date",
  modified: "Modified",
  requestedBy: "Requested By",
  priority: "Priority",
  internalTaskStatus: "Status",
  percentComplete: "% Complete",
  completionDate: "Completion Date",
  communityCommittee: "Community Committee",
  created: "Created",
  author: "Author",
  editor: "Editor",
  note: "Note",
  noteHistory: "Note History",
};

const decodeHtmlEntities = (str: string) => {
  const element = document.createElement("div");
  element.innerHTML = str;
  return element.textContent || element.innerText || "";
};

const splitNotes = (notes: string) => {
  const decodedNotes = decodeHtmlEntities(notes || "");

  // Match timestamps like 10/09/2025 03:00:00 PM
  const notePattern =
    /(\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{1,2}:\d{1,2} [APM]{2})/g;

  const parts = decodedNotes.split(notePattern).filter(Boolean);

  const formatted = parts.map((part, i) =>
    notePattern.test(part) ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
  );

  return <div style={{ whiteSpace: "pre-wrap" }}>{formatted}</div>;
};

const BoardTasks = observer(() => {
  const { apiStore, applicationStore, boardTaskStore } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<TasksListBlock>();
  const [searchText, setSearchText] = useState("");
  const [allStatuses, setAllStatuses] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("All Active");
  const [currentPage, setCurrentPage] = useState(1);
  const [columnOrder, setColumnOrder] = useState<ColumnKey[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<ColumnKey, boolean>
  >({
    id: true,
    title: true,
    internalTaskStatus: true,
    dueDate: true,
    modified: true,
    priority: true,
  });
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const navigate = useNavigate();
  const className = "Board Tasks";
  const BREAKPOINTS = { mobile: 0, desktop: 768 };
  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");

  useEffect(() => {
    if (response?.columnOrder && response?.columnSettings) {
      const order = response.columnOrder.filter((key) => ALLOWED_COLUMNS.includes(key));
      ALLOWED_COLUMNS.forEach((key) => {
        if (!order.includes(key)) {
          order.push(key);
        }
      });
      const settings = response?.columnSettings;
      setColumnOrder(order);
      const visibilityMap: Record<ColumnKey, boolean> = {
        id: true,
        title: true,
        internalTaskStatus: true,
        dueDate: true,
        modified: true,
        priority: true,
      };
      settings?.forEach((setting) => {
        const key = setting?.columnKey;
        if (key && ALLOWED_COLUMNS.includes(key)) {
          visibilityMap[key] = setting?.isVisible;
        }
      });
      setColumnVisibility(visibilityMap);
    }
  }, [response]);
  useEffect(() => {
    setCurrentPage(1);
  }, [response, searchText]);

  useEffect(() => {
    try {
      const filtered = (applicationStore.taskStatuses || [])
        .filter(Boolean)
        .map((s) => (s === "Cancelled" ? "Canceled" : s));

      const preferredOrder = ["All", "All Active"];

      const deduped = Array.from(new Set([...filtered, ...preferredOrder])).sort(
        (a, b) => {
          const indexA = preferredOrder.indexOf(a);
          const indexB = preferredOrder.indexOf(b);

          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }

          if (indexA !== -1) return -1;

          if (indexB !== -1) return 1;

          return String(a).localeCompare(String(b));
        }
      );

      setAllStatuses(deduped);

      if (!deduped.includes(selectedStatus)) {
        setSelectedStatus("All Active");
      }
    } catch (err) {
      console.error("Error fetching SharePoint statuses:", err);
    }
  }, [applicationStore.taskStatuses]);

  useEffect(() => {
    if (!allStatuses.length) return;
    const run = async () => {
      await fetchTasksForStatus(selectedStatus);
    };
    run();
  }, [selectedStatus, allStatuses]);

  const handleDragStart = (e: React.DragEvent, columnIndex: number) => {
    e.dataTransfer.setData("text/plain", columnIndex.toString());
    e.dataTransfer.effectAllowed = "move";
    setIsDragging(true);
    const dragElement = e.currentTarget.cloneNode(true) as HTMLElement;
    dragElement.style.opacity = "0.8";
    dragElement.style.transform = `rotate(${Constants.DRAG_ROTATION_DEG}deg)`;
    document.body.appendChild(dragElement);
    e.dataTransfer.setDragImage(dragElement, 0, 0);
    setTimeout(() => {
      document.body.removeChild(dragElement);
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== targetIndex) {
      setDragOverIndex(targetIndex);
    }
  };

  const handleDragEnter = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    setDragOverIndex(targetIndex);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));

    if (dragIndex !== dropIndex && !isNaN(dragIndex)) {
      const visibleColumns = columnOrder.filter(
        (colKey) => columnVisibility[colKey]
      );
      const newOrder = [...visibleColumns];
      const draggedColumn = newOrder[dragIndex];
      newOrder.splice(dragIndex, 1);
      newOrder.splice(dropIndex, 0, draggedColumn);
      const newFullOrder = [...columnOrder];
      let visibleIndex = 0;

      for (let i = 0; i < newFullOrder.length; i++) {
        if (columnVisibility[newFullOrder[i]]) {
          newFullOrder[i] = newOrder[visibleIndex];
          visibleIndex++;
        }
      }

      setColumnOrder(newFullOrder);
    }

    setIsDragging(false);
    setDragOverIndex(null);
  };

  const handleCheckboxChange = (colKey: string) => (e: CheckboxChangeEvent) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [colKey]: e.target.checked,
    }));
  };

  const handleCheckboxClick = (ev: React.MouseEvent) => {
    ev.stopPropagation();
  };

  const manageLayoutsMenu: MenuProps["items"] = useMemo(() => {
    const visibleKeys = columnOrder.filter((key) => ALLOWED_COLUMNS.includes(key));
    return visibleKeys.map((colKey) => ({
      key: colKey,
      label: (
        <Checkbox
          checked={columnVisibility[colKey]}
          onChange={handleCheckboxChange(colKey)}
          onClick={handleCheckboxClick}
        >
          {COLUMN_TITLES[colKey] || colKey}
        </Checkbox>
      ),
    }));
  }, [columnOrder, columnVisibility]);

  const fetchTasksForStatus = async (value: string) => {
    setResponse(undefined);
    setIsLoading(true);
    try {
      let statusFilter: string[] = [value];
      if (value === "All Active" && allStatuses.length) {
        statusFilter = allStatuses.filter(
          (s) => !["All", "All Active", "Completed", "Canceled", "Cancelled"].includes(s)
        );
      }
      const mappedFilter = statusFilter.flatMap((s) => (s === "Canceled" ? ["Canceled", "Cancelled"] : [s]));
      const { tasks, columnOrder, columnSettings } =
        await apiStore.BoardVueApiClient.getTaskListByStatusWithFilter(
          mappedFilter
        );
      setResponse({
        taskList: tasks,
        nextBlockUrl: "",
        columnOrder,
        columnSettings,
      });
    } catch (err) {
      console.error("Error fetching filtered SharePoint items:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveColumnLayout = async () => {
    if (!response?.columnOrder) {
      console.warn("No column order to save");
      return;
    }

    const layout: UserColumn[] = columnOrder.map((colKey, index) => ({
      columnKey: colKey,
      displayOrder: index,
      isVisible: columnVisibility[colKey],
      filterJson: selectedStatus,
    }));
    const body: LayoutColumnSettingsRequest = {
      UserId: Number(apiStore.configApi.userId),
      DateCreated: new Date().toISOString(),
      LayoutName: className,
      ColumnSettings: layout,
    };

    try {
      await apiStore.BoardVueApiClient.saveTaskLayoutPreferences(body);
      message.success("saved successfully", 2);
    } catch (err) {
      message.error("Failed to save layout:");
    }
  };

  const onRowClick = (item: LocalTask) => {
    boardTaskStore.setTaskModel({} as LocalTask);
    boardTaskStore.attachments = [];
    navigate(`/BoardVue/BoardTasks/Edit/${item.id}`);
  };

  const onCreateNewTask = () => {
    boardTaskStore.setTaskModel({} as LocalTask);
    boardTaskStore.attachments = [];
    navigate("/BoardVue/BoardTasks/Create");
  };
  const filteredData = useMemo(() => {
    if (!response?.taskList) return [];

    const base = !searchText
      ? response.taskList
      : response.taskList.filter((task) =>
          Object.values(task).some((value) =>
            String(value).toLowerCase().includes(searchText.toLowerCase())
          )
        );

    return [...base].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
  }, [response?.taskList, searchText]);

  const tableColumns = useMemo(() => {
    const visibleColumns = columnOrder.filter(
      (colKey) => ALLOWED_COLUMNS.includes(colKey) && columnVisibility[colKey]
    );

    return visibleColumns.map((colKey, index) => {
      const baseColumn = {
        title: (
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnter={(e) => handleDragEnter(e, index)}
            onDragLeave={handleDragLeave}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, index)}
            style={{
              cursor: isDragging ? "grabbing" : "grab",
              padding: "8px 0",
              borderRadius: "4px",
              backgroundColor:
                dragOverIndex === index ? "#e6f7ff" : "transparent",
              border:
                dragOverIndex === index
                  ? "2px dashed #1890ff"
                  : "2px solid transparent",
              transition: "all 0.2s ease",
              userSelect: "none",
              position: "relative",
              opacity: isDragging && dragOverIndex === index ? 0.5 : 1,
            }}
          >
            <Flex align="center" gap={8}>
              <span style={{ fontWeight: 500 }}>
                {COLUMN_TITLES[colKey] || colKey}
              </span>
            </Flex>
          </div>
        ),
        dataIndex: colKey,
        key: colKey,
        align: "left" as const,
        ellipsis: true,
        sorter: (a: any, b: any) => {
          const valA = a[colKey];
          const valB = b[colKey];
          if (valA == null) return -1;
          if (valB == null) return 1;
          return String(valA).localeCompare(String(valB));
        },
      };

      switch (colKey) {
        case "id":
        case "percentComplete":
          return {
            ...baseColumn,
            width: colKey === "id" ? 80 : 100,
            render: colKey === "percentComplete"
              ? (value: number) => <span>{value || 0}%</span>
              : undefined,
            sorter: (a: any, b: any) => {
              const valA = a[colKey];
              const valB = b[colKey];
              if (valA == null) return -1;
              if (valB == null) return 1;
              return (valA || 0) - (valB || 0);
            },
          };

        case "startDate":
        case "dueDate":
        case "modified":
        case "completionDate":
        case "created":
          return {
            ...baseColumn,
            width: 120,
            render: (value: string) => formatDateCell(value),
            sorter: (a: any, b: any) => {
              const valA = a[colKey] ? new Date(a[colKey]).getTime() : 0;
              const valB = b[colKey] ? new Date(b[colKey]).getTime() : 0;
              return valA - valB;
            },
          };

        case "priority":
        case "title":
        case "requestedBy":
        case "internalTaskStatus":
        case "communityCommittee":
        case "author":
        case "editor":
          return {
            ...baseColumn,
            width: colKey === "title" ? 250 : (colKey === "internalTaskStatus" || colKey === "priority" ? 120 : undefined),
            render: (value: string) => {
              const displayValue = colKey === "internalTaskStatus" && value === "Cancelled" ? "Canceled" : (value || "");
              return displayValue;
            },
            sorter: (a: any, b: any) => {
              const valA = a[colKey];
              const valB = b[colKey];
              if (valA == null) return -1;
              if (valB == null) return 1;
              return String(valA || "").localeCompare(String(valB || ""));
            },
          };
        case "noteHistory":
          return {
            ...baseColumn,
            render: (value: string) => splitNotes(value),
          };

        default:
          return {
            ...baseColumn,
            render: (value: any) => (value != null ? String(value) : ""),
          };
      }
    });
  }, [columnOrder, columnVisibility, dragOverIndex, isDragging]);

  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Flex
        justify="space-between"
        wrap="wrap"
        align="center"
        gap="16px"
        style={{ paddingBottom: 24 }}
      >
        <Flex wrap="wrap" align="center" gap="16px" className="w-mo-100">
          <h1 className="page-title">Board Tasks</h1>
          <Input
            className="serach-bar"
            prefix={
              <Button shape="circle" type="text" icon={<SearchOutlined />} />
            }
            variant="borderless"
            placeholder="Search"
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Flex>

        <Flex wrap="wrap" align="center" gap="16px" className="w-mo-100">
          <Select
            placeholder="Filter by status"
            className="select-box"
            value={selectedStatus}
            options={allStatuses.map((text) => ({ value: text, label: text }))}
            onChange={setSelectedStatus}
          />
          <Button size="middle" type="primary" onClick={onCreateNewTask}>
            Create
          </Button>
        </Flex>
      </Flex>
      <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
        <Dropdown menu={{ items: manageLayoutsMenu }} trigger={["click"]}>
          <Button icon={<EyeOutlined />}>Manage Columns</Button>
        </Dropdown>

        <Button type="primary" onClick={saveColumnLayout}>
          Save Layout
        </Button>

        <Button
          onClick={() => {
            const defaultVisibility: Record<string, boolean> = {};
            columnOrder.forEach((key) => {
              defaultVisibility[key] = true;
            });
            setColumnVisibility(defaultVisibility);
          }}
        >
          Show All Columns
        </Button>
      </div>
      {visibleColumnsCount > 0 ? (
        <Table
          className="table-wrapper"
          dataSource={
            filteredData?.map((el, index) => ({ ...el, key: el.id || index })) || []
          }
          scroll={{ x: process.env.REACT_APP_TABLE_SCROLL_WIDTH }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: false,
            responsive: true,
            current: currentPage,
            onChange: setCurrentPage,
            total: filteredData?.length || 0,
          }}
          columns={tableColumns}
          loading={isLoading}
          size={breakpoint === "mobile" ? "small" : "large"}
          onRow={(record) => ({
            onClick: () => onRowClick(record as LocalTask),
            style: { cursor: "pointer" },
          })}
          style={{
            width: '100%'
          }}
          components={{
            header: {
              cell: (props: any) => (
                <th
                  {...props}
                  style={{
                    ...props.style,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                />
              ),
            },
          }}
        />
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#888",
            border: "1px solid #f0f0f0",
            borderRadius: "8px",
          }}
        >
          No columns selected — please enable at least one column to view data.
        </div>
      )}

    </Space>
  );
});

export default BoardTasks;